import { Delete, Folder, KeyboardArrowDown, Sell } from '@mui/icons-material';
import { Divider, ListItemIcon, ListItemText, Menu } from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BulkActionButton, BulkActionMenuItem } from '~/components/bulk-actions';
import { focusableDisabledItems } from '~/components/menu-item';
import { useBulkActionIds } from '~/hooks/unique-id';

export type BulkActionsProps = {
  canDestroySelected: boolean;
  onDeleteContent: () => void;
  onMoveContent: () => void;
  onTagContent: () => void;
};

export const BulkActions = ({
  canDestroySelected,
  onDeleteContent,
  onMoveContent,
  onTagContent,
}: BulkActionsProps) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const { buttonId, menuId } = useBulkActionIds();

  return (
    <>
      <BulkActionButton
        ref={buttonRef}
        disableRipple
        id={buttonId}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={() => setOpen(true)}
      >
        {t('common.buttons.bulk_actions')}
        <KeyboardArrowDown />
      </BulkActionButton>
      <Menu
        id={menuId}
        anchorEl={buttonRef.current}
        open={open}
        onClose={() => setOpen(false)}
        MenuListProps={{
          ...focusableDisabledItems,
          'aria-labelledby': buttonId,
        }}
      >
        <BulkActionMenuItem
          onClick={() => {
            onMoveContent();
            setOpen(false);
          }}
        >
          <ListItemIcon>
            <Folder fontSize="small" />
          </ListItemIcon>
          {t('pages.content.actions.move_to_folder')}
        </BulkActionMenuItem>
        <BulkActionMenuItem
          onClick={() => {
            onTagContent();
            setOpen(false);
          }}
        >
          <ListItemIcon>
            <Sell fontSize="small" />
          </ListItemIcon>
          {t('common.tags.add_tag')}
        </BulkActionMenuItem>
        <Divider />
        <BulkActionMenuItem
          disabled={!canDestroySelected}
          onClick={() => {
            onDeleteContent();
            setOpen(false);
          }}
          tooltip={!canDestroySelected && t('pages.content.actions.cannot_delete_tooltip')}
        >
          <ListItemIcon>
            <Delete color="error" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: 'error' }}>
            {t('common.actions.delete')}
          </ListItemText>
        </BulkActionMenuItem>
      </Menu>
    </>
  );
};
